import {
    Box,
    Card,
    Stack,
    styled,
    Table,
    TableRow,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import AppModal from 'components/AppModal';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import PopUpConfirmation from 'page-sections/group-registration/PopUpConfirmation';
import columnShape from 'page-sections/register-group-list-operator/columnShape';
import HeadingArea from 'page-sections/register-group-list-operator/heading-area';
import SearchArea from 'page-sections/register-group-list-operator/search-area';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAsyncDebounce,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';

const HeadTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    '&:first-of-type': {
        paddingLeft: 24,
    },
    '&:last-of-type': {
        paddingRight: 24,
    },
}));

const BodyTableCell = styled(HeadTableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const RegisterGroupListOperator = () => {
    const [value, setValue] = useState('all');
    const [dataTable, setDataTable] = useState([]);
    const [filteredItem, setFilteredItem] = useState([]);
    const [dataToDelete, setDataToDelete] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [disableButtonPopUp, setDisableButtonPopUp] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const columns = useMemo(() => columnShape, []);
    const globalFilterFn = useMemo(
        () => (rows, _columnIds, filterValue) => {
            const q = String(filterValue || '').toLowerCase().trim();
            if (!q) return rows;
            return rows.filter((row) => {
                const o = row.original || {};
                const participants = Array.isArray(o.details) ? o.details : [];
                const participantText = participants
                    .flatMap((d) => [d.fullName, d.email])
                    .map((v) => String(v || ''))
                    .join(' ');
                const text = [
                    o.invoiceNumber,
                    o.fullName,
                    o.phoneNumber,
                    o.sponsor,
                    o.status?.label,
                    participantText,
                ]
                    .map((v) => String(v || ''))
                    .join(' ')
                    .toLowerCase();
                return text.includes(q);
            });
        },
        [],
    );
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        pageOptions,
        gotoPage,
        state,
        setGlobalFilter,
    } = useTable(
        { columns, data: filteredItem, globalFilter: globalFilterFn },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
    );

    const handleEdit = (rowData) => {
        navigate(`/dashboard/group-registration-operator/${rowData.id}`);
    };

    const handleDetail = (rowData) => {
        navigate(`/dashboard/group-registration-operator/${rowData.id}`);
    };

    const getData = async (param) => {
        await axiosInstance
            .get(`/api/register-groups`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                        'accessToken',
                    )}`,
                },
            })
            .then(({ data }) => {
                setDataTable(data);
                setFilteredItem(data);
            })
            .catch((err) => console.log(err));
    };
    useEffect(() => {
        getData();
        // eslint-disable-next-line
    }, []);

    const handleDelete = (data) => {
        setDataToDelete(data);
        setOpenModal(true);
    };
    const handleApprove = async () => {
        setDisableButtonPopUp(true);

        await axiosInstance
            .delete(`api/register-groups/${dataToDelete.id}`)
            .then(() => {
                handleCloseModal();
                getData();
            })
            .catch((err) => {
                handleCloseModal();
            });
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setDisableButtonPopUp(false);
    };

    const handleChange = (_, currentPageNo) => gotoPage(currentPageNo - 1);
    const changeTab = (_, newValue) => {
        setValue(newValue);
        if (newValue === 'all') {
            setFilteredItem(dataTable);
        } else {
            setFilteredItem(
                dataTable.filter((row) => row.status.value === newValue),
            );
        }
    };

    const [searchValue, setSearchValue] = useState(state.globalFilter);
    const handleSearch = useAsyncDebounce(
        (value) => setGlobalFilter(value || undefined),
        200,
    );

    return (
        <Box pt={2} pb={4}>
            <AppModal open={openModal} handleClose={handleCloseModal}>
                <PopUpConfirmation
                    disableButton={disableButtonPopUp}
                    handleCancel={handleCloseModal}
                    handleApprove={handleApprove}
                    data={dataToDelete}
                />
            </AppModal>

            <Card sx={{ py: 2 }}>
                <Box px={3}>
                    <HeadingArea
                        data={dataTable}
                        value={value}
                        changeTab={changeTab}
                    />
                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                    />
                </Box>

                <Scrollbar autoHide={true}>
                    <Table {...getTableProps()} sx={{ minWidth: 900 }}>
                        <TableHead
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    key={index}
                                    {...headerGroup.getHeaderGroupProps()}
                                >
                                    {headerGroup.headers.map(
                                        (column, index) => (
                                            <HeadTableCell
                                                key={index}
                                                {...column.getHeaderProps(
                                                    column.getSortByToggleProps(),
                                                )}
                                                sx={{
                                                    paddingY: 0,
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    borderBottom: 0,
                                                    width: column.width,
                                                    color: 'text.secondary',
                                                    minWidth: column.minWidth,
                                                    maxWidth: column.maxWidth,
                                                    '&:last-child': {
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            >
                                                {column.render('Header')}
                                            </HeadTableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableHead>

                        <TableBody {...getTableBodyProps()}>
                            {page.map((row, index) => {
                                prepareRow(row);
                                return (
                                    <TableRow
                                        {...row.getRowProps()}
                                        key={index}
                                    >
                                        {row.cells.map((cell, index) => {
                                            return (
                                                <BodyTableCell
                                                    key={index}
                                                    {...cell.getCellProps()}
                                                >
                                                    {cell.render('Cell', {
                                                        onClickEdit: handleEdit,
                                                        onClickDetail:
                                                            handleDetail,
                                                        handleDelete,
                                                    })}
                                                </BodyTableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        count={pageOptions.length}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default RegisterGroupListOperator;
